import { GAS_EMISSION_FACTOR_MT_PER_THERM } from './bhSourceControls';
import { activityMtCo2e } from './experimentalEmissions';
import type { CanonicalEnergyActivityYear } from './types';
import type { ChartDisplayMode } from './elecNonresidentialChartSegments';

export interface GasTotalChartSegments {
  residential: number;
  commercialCategory: number;
  industrialCategory: number | null;
  canonicalTotal: number;
  missingComponent: boolean;
}

export interface GasTotalChartSegmentOptions {
  /** When true, nonresidential stacks commercial + industrial (when industrial is reported). */
  includeIndustrial?: boolean;
}

function roundDisplay(n: number, mode: ChartDisplayMode): number {
  if (mode === 'emissions') return Math.round(n * 10) / 10;
  return Math.round(n);
}

function segmentFromTherms(value: number | null | undefined, mode: ChartDisplayMode): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (mode === 'emissions') {
    const mt = activityMtCo2e(
      {
        value,
        unit: 'therms',
        coverage: 'complete_observed',
        sourceClass: 'utility_observed',
        methodId: '',
        sourceNote: '',
        includedCategories: [],
        missingCategories: [],
      },
      'natural_gas'
    );
    return mt != null ? roundDisplay(mt, mode) : null;
  }
  return roundDisplay(value, mode);
}

export function buildGasTotalChartSegments(
  row: CanonicalEnergyActivityYear,
  mode: ChartDisplayMode,
  options?: GasTotalChartSegmentOptions
): GasTotalChartSegments {
  const resSeg = segmentFromTherms(row.residential.value, mode);
  const comSeg = segmentFromTherms(row.commercial.value, mode);
  const indTherms = row.commercial.industrialComponent ?? row.industrial.value;
  const indEligible =
    indTherms != null &&
    Number.isFinite(indTherms) &&
    indTherms > 0 &&
    (row.industrial.coverage === 'complete_observed' ||
      row.industrial.methodId.startsWith('burbank_gas_ind_socal'));
  const indSeg =
    options?.includeIndustrial && indEligible ? segmentFromTherms(indTherms!, mode) : null;
  const missingComponent = resSeg == null || comSeg == null;
  const canonicalTotal = missingComponent
    ? Number.NaN
    : resSeg + comSeg + (indSeg ?? 0);
  return {
    residential: resSeg ?? Number.NaN,
    commercialCategory: comSeg ?? Number.NaN,
    industrialCategory: indSeg,
    canonicalTotal,
    missingComponent,
  };
}

function fmtTherms(n: number): string {
  return Math.round(n).toLocaleString();
}

function fmtMt(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/** @deprecated Use gasTotalYearTooltipLinesRevised from capTotalChartTooltips via rev2 chart bundle. */
export { gasTotalYearTooltipLinesRevised as gasTotalYearTooltipLines } from './capTotalChartTooltips';
